import React, { useState } from 'react';
import { Download, Loader2, CheckCircle, AlertCircle, Code2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

export const DownloadSourceButton = ({ variant = 'default', size = 'default', className = '' }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null); // 'success' | 'error' | null

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadStatus(null);

    try {
      // Get backend URL from environment
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const downloadUrl = `${backendUrl}/api/download-source`;

      toast.info('Preparing source code download...');

      // Fetch the ZIP file
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/zip',
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      // Get the blob
      const blob = await response.blob();
      const sizeInKB = (blob.size / 1024).toFixed(2);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'notracktools-source.zip';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDownloadStatus('success');
      toast.success(`Source code downloaded successfully! (${sizeInKB} KB)`);

      // Reset status after 3 seconds
      setTimeout(() => setDownloadStatus(null), 3000);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadStatus('error');
      toast.error('Failed to download source code. Please try again.');
      
      // Reset status after 3 seconds
      setTimeout(() => setDownloadStatus(null), 3000);
    } finally {
      setIsDownloading(false);
    }
  };

  const getIcon = () => {
    if (isDownloading) return <Loader2 className="w-4 h-4 animate-spin" />;
    if (downloadStatus === 'success') return <CheckCircle className="w-4 h-4" />;
    if (downloadStatus === 'error') return <AlertCircle className="w-4 h-4" />;
    return <Download className="w-4 h-4" />;
  };

  const getButtonText = () => {
    if (isDownloading) return 'Downloading...';
    if (downloadStatus === 'success') return 'Downloaded!';
    if (downloadStatus === 'error') return 'Failed';
    return 'Download Source';
  };

  return (
    <Button
      onClick={handleDownload}
      disabled={isDownloading}
      variant={variant}
      size={size}
      className={className}
    >
      {getIcon()}
      <span className="ml-2">{getButtonText()}</span>
    </Button>
  );
};

export default DownloadSourceButton;
